import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useAboutPage, useUpdateAboutPage } from '../hooks/useCmsPages';
import Swal from 'sweetalert2';

const About = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    title: { ar: '', en: '' },
    text: { ar: '', en: '' },
    rate: 0,
    ratePhoto: null,
    photo: null,
    ourMission: {
      title: { ar: '', en: '' },
      text: { ar: '', en: '' },
      ourValuesPoints: [],
      upperPhoto: null,
      bottomPhoto: null,
      conversationPerMonthNumber: 0,
      projectNumber: 0
    },
    ourClientText: { ar: '', en: '' }
  });

  // Fetch about data using React Query
  const {
    data: aboutData,
    isLoading,
    error,
  } = useAboutPage();

  // Update about mutation
  const updateAboutMutation = useUpdateAboutPage();

  // Initialize edited data when about data is loaded
  React.useEffect(() => {
    if (aboutData?.data && !isEditing) {
      // Ensure Arabic fields have default values to prevent validation errors
      const dataWithArabicDefaults = {
        ...aboutData.data,
        title: {
          ar: aboutData.data.title?.ar || aboutData.data.title?.en || 'About Us',
          en: aboutData.data.title?.en || ''
        },
        text: {
          ar: aboutData.data.text?.ar || aboutData.data.text?.en || 'About us description',
          en: aboutData.data.text?.en || ''
        },
        ourMission: {
          ...aboutData.data.ourMission,
          title: {
            ar: aboutData.data.ourMission?.title?.ar || aboutData.data.ourMission?.title?.en || 'Our Mission',
            en: aboutData.data.ourMission?.title?.en || ''
          },
          text: {
            ar: aboutData.data.ourMission?.text?.ar || aboutData.data.ourMission?.text?.en || 'Our mission description',
            en: aboutData.data.ourMission?.text?.en || ''
          },
          ourValuesPoints: (aboutData.data.ourMission?.ourValuesPoints || []).map(point => ({
            ar: point.ar || point.en || 'Value point',
            en: point.en || ''
          }))
        },
        ourClientText: {
          ar: aboutData.data.ourClientText?.ar || aboutData.data.ourClientText?.en || 'Our client description',
          en: aboutData.data.ourClientText?.en || ''
        }
      };
      setEditedData(dataWithArabicDefaults);
    }
  }, [aboutData, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    // Ensure Arabic fields have default values when editing
    const dataWithArabicDefaults = {
      ...aboutData.data,
      title: {
        ar: aboutData.data.title?.ar || aboutData.data.title?.en || 'About Us',
        en: aboutData.data.title?.en || ''
      },
      text: {
        ar: aboutData.data.text?.ar || aboutData.data.text?.en || 'About us description',
        en: aboutData.data.text?.en || ''
      },
      ourMission: {
        ...aboutData.data.ourMission,
        title: {
          ar: aboutData.data.ourMission?.title?.ar || aboutData.data.ourMission?.title?.en || 'Our Mission',
          en: aboutData.data.ourMission?.title?.en || ''
        },
        text: {
          ar: aboutData.data.ourMission?.text?.ar || aboutData.data.ourMission?.text?.en || 'Our mission description',
          en: aboutData.data.ourMission?.text?.en || ''
        },
        ourValuesPoints: (aboutData.data.ourMission?.ourValuesPoints || []).map(point => ({
          ar: point.ar || point.en || 'Value point',
          en: point.en || ''
        }))
      },
      ourClientText: {
        ar: aboutData.data.ourClientText?.ar || aboutData.data.ourClientText?.en || 'Our client description',
        en: aboutData.data.ourClientText?.en || ''
      }
    };
    setEditedData(dataWithArabicDefaults);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original data with Arabic defaults
    if (aboutData?.data) {
      const dataWithArabicDefaults = {
        ...aboutData.data,
        title: {
          ar: aboutData.data.title?.ar || aboutData.data.title?.en || 'About Us',
          en: aboutData.data.title?.en || ''
        },
        text: {
          ar: aboutData.data.text?.ar || aboutData.data.text?.en || 'About us description',
          en: aboutData.data.text?.en || ''
        },
        ourMission: {
          ...aboutData.data.ourMission,
          title: {
            ar: aboutData.data.ourMission?.title?.ar || aboutData.data.ourMission?.title?.en || 'Our Mission',
            en: aboutData.data.ourMission?.title?.en || ''
          },
          text: {
            ar: aboutData.data.ourMission?.text?.ar || aboutData.data.ourMission?.text?.en || 'Our mission description',
            en: aboutData.data.ourMission?.text?.en || ''
          },
          ourValuesPoints: (aboutData.data.ourMission?.ourValuesPoints || []).map(point => ({
            ar: point.ar || point.en || 'Value point',
            en: point.en || ''
          }))
        },
        ourClientText: {
          ar: aboutData.data.ourClientText?.ar || aboutData.data.ourClientText?.en || 'Our client description',
          en: aboutData.data.ourClientText?.en || ''
        }
      };
      setEditedData(dataWithArabicDefaults);
    }
  };

  const handleSave = async () => {
    try {
      // Ensure all Arabic fields have values before saving
      const dataToSave = {
        ...editedData,
        title: {
          ar: editedData.title.ar || editedData.title.en || 'About Us',
          en: editedData.title.en || ''
        },
        text: {
          ar: editedData.text.ar || editedData.text.en || 'About us description',
          en: editedData.text.en || ''
        },
        ourMission: {
          ...editedData.ourMission,
          title: {
            ar: editedData.ourMission.title.ar || editedData.ourMission.title.en || 'Our Mission',
            en: editedData.ourMission.title.en || ''
          },
          text: {
            ar: editedData.ourMission.text.ar || editedData.ourMission.text.en || 'Our mission description',
            en: editedData.ourMission.text.en || ''
          },
          ourValuesPoints: editedData.ourMission.ourValuesPoints.map(point => ({
            ar: point.ar || point.en || 'Value point',
            en: point.en || ''
          }))
        },
        ourClientText: {
          ar: editedData.ourClientText.ar || editedData.ourClientText.en || 'Our client description',
          en: editedData.ourClientText.en || ''
        }
      };

      await updateAboutMutation.mutateAsync(dataToSave);
      setIsEditing(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'About page updated successfully!',
        timer: 2000,
        showConfirmButton: false,
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#f59e0b] text-white font-bold py-2 px-4 rounded',
        }
      });
    } catch (error) {
      console.error('Error updating about page:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update about page',
        confirmButtonText: 'OK',
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        }
      });
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedFieldChange = (parent, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleValuePointChange = (index, value) => {
    setEditedData(prev => ({
      ...prev,
      ourMission: {
        ...prev.ourMission,
        ourValuesPoints: prev.ourMission.ourValuesPoints.map((point, i) => 
          i === index ? { 
            ...point, 
            en: value,
            ar: point.ar || value || 'Value point' // Keep Arabic value or use English as fallback
          } : point
        )
      }
    }));
  };

  const addValuePoint = () => {
    setEditedData(prev => ({
      ...prev,
      ourMission: {
        ...prev.ourMission,
        ourValuesPoints: [...prev.ourMission.ourValuesPoints, { 
          ar: 'Value point', // Default Arabic value
          en: '' 
        }]
      }
    }));
  };

  const removeValuePoint = (index) => {
    setEditedData(prev => ({
      ...prev,
      ourMission: {
        ...prev.ourMission,
        ourValuesPoints: prev.ourMission.ourValuesPoints.filter((_, i) => i !== index)
      }
    }));
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="About Us"
            description="Manage about us page content"
            user={user}
          />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="About Us"
          description="Manage about us page content"
        />

        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Content Management</h2>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md transition duration-200"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                      disabled={updateAboutMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateAboutMutation.isPending}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200 disabled:opacity-50"
                    >
                      {updateAboutMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Main About Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">About Us</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title (English)
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.title?.en || ''}
                    onChange={(e) => handleFieldChange('title', { ...editedData.title, en: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter title in English"
                  />
                ) : (
                  <div className="text-gray-900 font-medium">{aboutData?.data?.title?.en || 'No title set'}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (English)
                </label>
                {isEditing ? (
                  <Editor
                    value={editedData.text?.en || ''}
                    onTextChange={(e) => handleFieldChange('text', { ...editedData.text, en: e.htmlValue })}
                    style={{ height: '200px' }}
                    className="w-full border border-gray-300 rounded-md"
                  />
                ) : (
                  <div 
                    className="text-gray-700 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: aboutData?.data?.text?.en || 'No description set' }}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={editedData.rate || 0}
                    onChange={(e) => handleFieldChange('rate', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter rating"
                  />
                ) : (
                  <div className="text-gray-900">{aboutData?.data?.rate || 0}/5</div>
                )}
              </div>
            </div>

            {/* Our Mission Section */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">Our Mission</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mission Title (English)
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.ourMission?.title?.en || ''}
                    onChange={(e) => handleNestedFieldChange('ourMission', 'title', { ...editedData.ourMission.title, en: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter mission title in English"
                  />
                ) : (
                  <div className="text-gray-900 font-medium">{aboutData?.data?.ourMission?.title?.en || 'No mission title set'}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mission Description (English)
                </label>
                {isEditing ? (
                  <Editor
                    value={editedData.ourMission?.text?.en || ''}
                    onTextChange={(e) => handleNestedFieldChange('ourMission', 'text', { ...editedData.ourMission.text, en: e.htmlValue })}
                    style={{ height: '200px' }}
                    className="w-full border border-gray-300 rounded-md"
                  />
                ) : (
                  <div 
                    className="text-gray-700 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: aboutData?.data?.ourMission?.text?.en || 'No mission description set' }}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Our Values (English)
                </label>
                {isEditing ? (
                  <div className="space-y-2">
                    {editedData.ourMission?.ourValuesPoints?.map((point, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={point.en || ''}
                          onChange={(e) => handleValuePointChange(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Enter value point in English"
                        />
                        <button
                          type="button"
                          onClick={() => removeValuePoint(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addValuePoint}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      + Add Value Point
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {aboutData?.data?.ourMission?.ourValuesPoints?.map((point, index) => (
                      <div key={index} className="text-gray-700">• {point.en}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conversations per Month
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editedData.ourMission?.conversationPerMonthNumber || 0}
                      onChange={(e) => handleNestedFieldChange('ourMission', 'conversationPerMonthNumber', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter number"
                    />
                  ) : (
                    <div className="text-gray-900 font-medium">{aboutData?.data?.ourMission?.conversationPerMonthNumber || 0}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Number
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editedData.ourMission?.projectNumber || 0}
                      onChange={(e) => handleNestedFieldChange('ourMission', 'projectNumber', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter number"
                    />
                  ) : (
                    <div className="text-gray-900 font-medium">{aboutData?.data?.ourMission?.projectNumber || 0}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Our Client Section */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">Our Client</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Description (English)
                </label>
                {isEditing ? (
                  <Editor
                    value={editedData.ourClientText?.en || ''}
                    onTextChange={(e) => handleFieldChange('ourClientText', { ...editedData.ourClientText, en: e.htmlValue })}
                    style={{ height: '200px' }}
                    className="w-full border border-gray-300 rounded-md"
                  />
                ) : (
                  <div 
                    className="text-gray-700 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: aboutData?.data?.ourClientText?.en || 'No client description set' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default About; 